import {useEffect, useMemo, useRef} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {TransitionDirection} from '@Panels/transitionable-panel-v2/transitionable-panel-v2.types';
import type {
  CheckoutDragHandle,
  CheckoutFlowController,
  CheckoutFlowInitializationHandlers,
  CheckoutFlowRenderState,
  CheckoutModalDimensions,
  CheckoutRequest,
  PendingCheckoutExit,
  SubscriptionCheckoutResult,
  UseCheckoutFlowOptions,
} from '@Components/checkout-flow/checkout-flow.types';
import {CheckoutMode, CheckoutStep, PreflightOutcome, StripeSessionMode} from '@Components/checkout-flow/checkout-flow.types';
import {
  goToCheckoutStep,
  resetCheckoutFlow,
  selectCheckoutFlow,
  setCheckoutConfirmation,
  setCheckoutError,
  setCheckoutLoading,
  setEffectiveCheckoutMode,
  setStripeSession,
  startCheckoutFlow,
} from '@Components/checkout-flow/checkout-flow-reducer';
import {canCurrentUserBuyCredits, getCheckoutSessionMode, getUserCurrentSubscriptionStatus, loadCheckoutDialogData} from '@Components/checkout-flow/checkout-flow-thunk';
import {
  getFlowChrome,
  getInitialStepForRequest,
  getModalHeightForStep,
  getModalMaxHeightForStep,
  getModalMobileHeightForStep,
  getModalWidthForStep,
  getPreviousStep,
  isCheckoutBottomSheet,
} from '@Components/checkout-flow/checkout-flow.library';
import useWindowSize from '@Hooks/useWindowSize';
import {isPremiumUser} from '@Utils/user.util';
import {handleCheckoutExit} from '@Components/checkout-flow/checkout-flow-completion.library';
import {BUY_CREDITS_REQUEST_TRIAL_CONVERSION_SOURCE} from '@Components/checkout-flow/checkout-flow.types';
import {openConfirmationModal} from '@Modals/confirmation-modal';
import {openMessageModal} from '@Modals/message-modal';
import {openErrorModal} from '@Modals/error-modal';

const STRIPE_PAYMENT_VENDOR = 'stripe';

export const useCheckoutFlow = (request: CheckoutRequest | null, {onExit, onBackFromFirstStep}: UseCheckoutFlowOptions): CheckoutFlowRenderState => {
  const dispatch = useAppDispatch();
  const flow = useAppSelector(selectCheckoutFlow);
  const {windowWidth} = useWindowSize();
  const hasExitedRef = useRef(false);
  const wasConfirmationShownRef = useRef(false);
  const pendingExitRef = useRef<PendingCheckoutExit | null>(null);

  if (flow.step === CheckoutStep.PREMIUM_CONFIRMATION) {
    wasConfirmationShownRef.current = true;
  }

  const controller = useMemo<CheckoutFlowController>(() => {
    const exit = (): void => {
      if (request === null || hasExitedRef.current) {
        return;
      }
      hasExitedRef.current = true;
      handleCheckoutExit({request, wasConfirmationShown: wasConfirmationShownRef.current});
      dispatch(resetCheckoutFlow());
      onExit();
    };

    const dismiss = (): void => {
      if (hasExitedRef.current) {
        return;
      }
      hasExitedRef.current = true;
      dispatch(resetCheckoutFlow());
      onExit();
    };

    return {
      goToStep: (step: CheckoutStep, direction: TransitionDirection = TransitionDirection.FORWARD): void => {
        dispatch(goToCheckoutStep({step, direction}));
      },
      goBack: (): void => {
        const previousStep = getPreviousStep(flow.step, flow.dialogData, flow.addresses.length > 0);
        if (previousStep !== null) {
          dispatch(goToCheckoutStep({step: previousStep, direction: TransitionDirection.BACKWARD}));
          return;
        }
        if (canHostTakeBack(flow.step) && onBackFromFirstStep !== undefined) {
          hasExitedRef.current = true;
          dispatch(resetCheckoutFlow());
          onBackFromFirstStep();
        }
      },
      showPremiumConfirmation: (result: SubscriptionCheckoutResult): void => {
        dispatch(setCheckoutConfirmation({ga4PurchaseEventParams: result.ga4PurchaseEventParams ?? null, trackingData: result.trackingData ?? null}));
        dispatch(goToCheckoutStep({step: CheckoutStep.PREMIUM_CONFIRMATION, direction: TransitionDirection.FORWARD}));
      },
      fail: (title: string, message: string): void => {
        dispatch(setCheckoutError({title, message}));
      },
      exit,
      dismiss,
    };
  }, [request, flow.step, flow.dialogData, flow.addresses.length, dispatch, onExit, onBackFromFirstStep]);

  useEffect(() => {
    settlePendingExit(request);
    if (request === null) {
      return undefined;
    }

    hasExitedRef.current = false;
    wasConfirmationShownRef.current = false;
    dispatch(startCheckoutFlow({mode: request.mode, promoCode: request.mode === CheckoutMode.SUBSCRIPTION ? request.promoCode : ''}));

    let isCancelled = false;
    void initializeCheckoutFlow(request, {
      isCancelled: () => isCancelled,
      abort: controller.exit,
      dismiss: controller.dismiss,
    });

    return (): void => {
      isCancelled = true;
      scheduleExit(request, controller.exit);
    };
  }, [request]);

  /**
   * An unmount without an explicit close (outside-click dismiss) still has to exit the flow, but React StrictMode
   * simulates an unmount right after mounting, so the exit is deferred a tick and dropped when the same request
   * re-mounts immediately.
   */
  const scheduleExit = (exitingRequest: CheckoutRequest, exit: () => void): void => {
    const timeoutId = window.setTimeout((): void => {
      pendingExitRef.current = null;
      exit();
    }, 0);
    pendingExitRef.current = {request: exitingRequest, exit, timeoutId};
  };

  const settlePendingExit = (nextRequest: CheckoutRequest | null): void => {
    const pendingExit = pendingExitRef.current;
    if (pendingExit === null) {
      return;
    }
    window.clearTimeout(pendingExit.timeoutId);
    pendingExitRef.current = null;
    if (pendingExit.request !== nextRequest) {
      pendingExit.exit();
    }
  };

  const initializeCheckoutFlow = async (activeRequest: CheckoutRequest, {isCancelled, abort, dismiss}: CheckoutFlowInitializationHandlers): Promise<void> => {
    try {
      const preflight = await runPreflight(activeRequest);
      if (isCancelled()) {
        return;
      }
      if (preflight === PreflightOutcome.ABORT) {
        abort();
        return;
      }
      if (preflight === PreflightOutcome.DISMISS) {
        dismiss();
        return;
      }

      if (activeRequest.mode === CheckoutMode.PAYMENT_PROCESSING) {
        await initializePaymentProcessing(activeRequest.sessionId);
        activeRequest.onOpen?.();
        return;
      }

      const dialogData = await dispatch(loadCheckoutDialogData({request: activeRequest})).unwrap();
      if (isCancelled()) {
        return;
      }
      if (activeRequest.mode === CheckoutMode.PREMIUM_CONFIRMATION) {
        dispatch(setCheckoutConfirmation({ga4PurchaseEventParams: activeRequest.ga4PurchaseEventParams ?? null, trackingData: activeRequest.trackingData ?? null}));
      }
      dispatch(goToCheckoutStep({step: getInitialStepForRequest(activeRequest, dialogData), direction: TransitionDirection.FORWARD}));
      dispatch(setCheckoutLoading(false));
      activeRequest.onOpen?.();
    } catch {
      if (!isCancelled()) {
        dispatch(setCheckoutError({title: window.i18next.t('pmwjs_something_went_wrong'), message: window.i18next.t('pmwjs_error_processing_request_reload_try_again')}));
      }
    }
  };

  const initializePaymentProcessing = async (sessionId: string): Promise<void> => {
    const {mode} = await getCheckoutSessionMode(sessionId);
    dispatch(setEffectiveCheckoutMode(getEffectiveModeForStripeSession(mode)));
    dispatch(setStripeSession({sessionId, clientSecret: ''}));
    dispatch(goToCheckoutStep({step: CheckoutStep.FINALIZING, direction: TransitionDirection.FORWARD}));
    dispatch(setCheckoutLoading(false));
  };

  return {
    request,
    isActive: request !== null && flow.isActive,
    step: flow.step,
    isLoading: flow.isLoading,
    transitionDirection: flow.transitionDirection,
    contentKey: getContentKey(flow.step, flow.isCreditBenefitsViewOpen),
    modalWidth: getModalWidthForStep(flow.step, windowWidth),
    canGoBack: getPreviousStep(flow.step, flow.dialogData, flow.addresses.length > 0) !== null || (onBackFromFirstStep !== undefined && canHostTakeBack(flow.step)),
    ...getFlowChrome(flow.step, windowWidth, flow.isCreditBenefitsViewOpen),
    controller,
  };
};

/** The compact credit-benefits view replaces the pack chooser in place, so it needs its own key to animate in. */
const getContentKey = (step: CheckoutStep, isCreditBenefitsViewOpen: boolean): string => {
  if (step === CheckoutStep.CREDIT_PACKS && isCreditBenefitsViewOpen) {
    return `checkout-${step}-benefits`;
  }
  return `checkout-${step}`;
};

const getEffectiveModeForStripeSession = (sessionMode: StripeSessionMode): CheckoutMode => {
  if (sessionMode === StripeSessionMode.SUBSCRIPTION) {
    return CheckoutMode.SUBSCRIPTION;
  }
  if (sessionMode === StripeSessionMode.PAYMENT) {
    return isPremiumUser() ? CheckoutMode.CREDIT_PACK : CheckoutMode.DOWNLOAD;
  }
  return CheckoutMode.PAYMENT_PROCESSING;
};

/**
 * Gates copied from the legacy public entry points: a non-Stripe active subscription blocks a new (non-trial)
 * subscription checkout, an unpaid cancelled one asks first, and non-admin team members cannot buy credit packs.
 * ABORT closes the flow through the request's close callback; DISMISS closes it silently (legacy never called
 * the close callback when diverting a credit-pack purchase).
 */
const runPreflight = async (request: CheckoutRequest): Promise<PreflightOutcome> => {
  if (request.mode === CheckoutMode.SUBSCRIPTION && !request.isTrial) {
    return runSubscriptionPreflight();
  }
  if (request.mode === CheckoutMode.CREDIT_PACK) {
    return runCreditPackPreflight(request.onOpen);
  }
  return PreflightOutcome.PROCEED;
};

const runSubscriptionPreflight = async (): Promise<PreflightOutcome> => {
  let status;
  try {
    status = await getUserCurrentSubscriptionStatus();
  } catch {
    openErrorModal({});
    return PreflightOutcome.ABORT;
  }

  if (status === null || status.vendor === STRIPE_PAYMENT_VENDOR || (status.isInactive && !status.awaitingPayment)) {
    return PreflightOutcome.PROCEED;
  }

  if (status.awaitingPayment) {
    return new Promise((resolve) => {
      openConfirmationModal({
        infoText: window.i18next.t('pmwjs_awaiting_payment_for_cancelled_subscription'),
        showCloseButton: false,
        showBackButton: false,
        onConfirm: () => {
          resolve(PreflightOutcome.PROCEED);
        },
        onCancel: () => {
          resolve(PreflightOutcome.ABORT);
        },
      });
    });
  }

  openMessageModal({text: window.i18next.t('pmwjs_premium_subscription_already_active')});
  return PreflightOutcome.ABORT;
};

const runCreditPackPreflight = async (onOpen?: () => void): Promise<PreflightOutcome> => {
  const canBuyCredits = await canCurrentUserBuyCredits();
  if (canBuyCredits) {
    return PreflightOutcome.PROCEED;
  }
  onOpen?.();
  window.PMW.premiumDialogs.openRequestTrialConversionDialog(BUY_CREDITS_REQUEST_TRIAL_CONVERSION_SOURCE);
  return PreflightOutcome.DISMISS;
};

/** A host may take the user back to its own content only before payment has started. */
const canHostTakeBack = (step: CheckoutStep): boolean => {
  return step === CheckoutStep.EMAIL_REQUIRED || step === CheckoutStep.CREDIT_PACKS || step === CheckoutStep.PAYMENT_OPTIONS || step === CheckoutStep.STRIPE_EMBEDDED;
};

/**
 * A host keeps its own width until the flow knows which step it opens on, so swapping the checkout in widens the
 * modal once — to the step's width — instead of snapping through the loading step's width on the way there. The
 * height hands over straight away: the flow's panel transitions its own height, and only a modal that is already
 * hugging its content follows that transition rather than jumping when the host lets go.
 */
export const useCheckoutModalDimensions = (defaults: CheckoutModalDimensions): CheckoutModalDimensions => {
  const isActive = useAppSelector((state) => state.checkoutFlow.isActive);
  const isLoading = useAppSelector((state) => state.checkoutFlow.isLoading);
  const step = useAppSelector((state) => state.checkoutFlow.step);
  const isCreditBenefitsViewOpen = useAppSelector((state) => state.checkoutFlow.isCreditBenefitsViewOpen);
  const {windowWidth} = useWindowSize();
  if (!isActive) {
    return defaults;
  }
  return {
    width: isLoading ? defaults.width : getModalWidthForStep(step, windowWidth),
    height: getModalHeightForStep(step, windowWidth),
    mobileHeight: getModalMobileHeightForStep(step, windowWidth, isCreditBenefitsViewOpen),
    maxHeight: getModalMaxHeightForStep(step),
  };
};

/**
 * The hand-off draws the sheet's grab handle on every mobile frame. It turns light over the credit-benefits
 * view, which paints its own dark gradient right up to the sheet's edges.
 */
export const useCheckoutDragHandle = (): CheckoutDragHandle => {
  const isCreditBenefitsViewOpen = useAppSelector((state) => state.checkoutFlow.isCreditBenefitsViewOpen);
  const {windowWidth} = useWindowSize();
  return {
    isVisible: isCheckoutBottomSheet(windowWidth),
    isOnDarkPanel: isCreditBenefitsViewOpen,
  };
};
